import { noteDegradation } from "./degradations";

/**
 * Guarded access to the platform APIs the analytics pipeline sits on.
 *
 * Every event runs through these on the hot path, and `track` is
 * fire-and-forget — so an unguarded throw here is 100% silent analytics loss
 * with no signal that it happened.
 */

/**
 * Tail of the same-tab fallback mutex, keyed by lock name.
 *
 * Each acquisition chains onto the previous one, so callbacks run in arrival
 * order rather than interleaving. Only used when the Web Locks API is missing.
 */
const fallbackLockTails = new Map<string, Promise<void>>();

/**
 * Runs `mutate` under the named Web Lock, falling back to a same-tab mutex when
 * the Web Locks API is unavailable.
 *
 * Older iOS WKWebView has no `navigator.locks`, and `clientOs: "ios"` /
 * `client: "mobile_webview"` are first-class registry values — WebView is
 * explicitly in scope. Both call sites await this inside `track`/`flushNow`,
 * which are fire-and-forget, so an unguarded TypeError here is silent total
 * loss from the first event onward.
 *
 * The fallback keeps mutual exclusion within the tab but cannot provide it
 * across tabs. That is a real reduction, not a full substitute: two tabs can
 * still interleave a read-modify-write on the shared queue and lose an event.
 * Within a tab is where the contention actually is, though — the queue is
 * touched on every click and on the flush timer — and a same-tab mutex is the
 * most that is recoverable without the API.
 */
export async function withQueueLock(
  name: string,
  mutate: () => Promise<void>,
): Promise<void> {
  if (typeof navigator.locks?.request === "function") {
    await navigator.locks.request(name, mutate);
    return;
  }

  noteDegradation("web-locks");
  const previous = fallbackLockTails.get(name) ?? Promise.resolve();
  // Chain off a swallowed copy of the previous link. Without the catch, one
  // rejected callback would poison every later acquisition on this name and
  // drop all subsequent events — the caller still sees its own rejection
  // below, it just does not become the next waiter's problem.
  const run = previous.catch(() => undefined).then(mutate);
  fallbackLockTails.set(
    name,
    run.catch(() => undefined),
  );
  await run;
}

/**
 * Returns a v4-shaped UUID, falling back to `Math.random` when
 * `crypto.randomUUID` is unavailable.
 *
 * `randomUUID` is exposed only in a secure context. Clients.App is served over
 * HTTPS, but a webview host page, an IP-address dev origin, or an embedded
 * frame need not be — and there it is `undefined`, so calling it throws a
 * TypeError inside `buildEvent` and takes the whole event with it.
 *
 * The fallback is not cryptographically random, which is fine for this use: the
 * value is a correlation id for grouping a tab's events, never a token or a
 * security boundary. Collision risk across tabs is the only cost.
 */
export function getRandomUuid(): string {
  if (typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  noteDegradation("crypto-random-uuid");
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const nibble = Math.floor(Math.random() * 16);
    // The variant nibble is constrained to 8/9/a/b; the rest are free.
    const value = char === "x" ? nibble : (nibble & 0x3) | 0x8;
    return value.toString(16);
  });
}
